import React from 'react';
import clsx from 'clsx';
import { countries } from '@dashboard/services/countries/countries';
import { FormElement, Label } from '@dashboard/components/Form';
import { Checkbox, Listbox, Radio, Select } from '@dashboard/components/Formik';
import FormError from '@dashboard/components/Formik/components/FormError';

export default function AddressFieldSettings({ fieldKey, values, setFieldValue }) {
  const isDual = values.collect === 'billing_and_shipping';

  return (
    <div className="space-y-3 mt-4 mb-4">
      <FormElement>
        <Label for={`${fieldKey}_defaultCountry`}>Default country</Label>
        <Select
          id={`${fieldKey}_defaultCountry`}
          name="defaultCountry"
          options={[
            { value: '', label: 'Detect from visitor' },
            ...countries.map(country => ({ value: country.alpha2, label: country.name })),
          ]}
        />
        <FormError name="defaultCountry" />
      </FormElement>

      <FormElement>
        <Radio.Group name="collect" id={`${fieldKey}_collect`}>
          <Radio.Wrapper>
            <Radio.Input id={`${fieldKey}_collect_billing`} name="collect" value="billing" />
            <Radio.Label htmlFor={`${fieldKey}_collect_billing`}>
              Billing addresses only
            </Radio.Label>
          </Radio.Wrapper>
          <Radio.Wrapper>
            <Radio.Input
              id={`${fieldKey}_collect_billing_and_shipping`}
              name="collect"
              value="billing_and_shipping"
            />
            <Radio.Label htmlFor={`${fieldKey}_collect_billing_and_shipping`}>
              Billing and shipping addresses
            </Radio.Label>
          </Radio.Wrapper>
        </Radio.Group>
        <FormError name="collect" />
      </FormElement>

      <div className="space-y-2">
        <Checkbox.Wrapper>
          <Checkbox
            id={`${fieldKey}_limitBillingCountries.enabled`}
            name="limitBillingCountries.enabled"
          />
          <Checkbox.Label htmlFor={`${fieldKey}_limitBillingCountries.enabled`}>
            Limit billing countries
          </Checkbox.Label>
        </Checkbox.Wrapper>

        {values.limitBillingCountries?.enabled && (
          <div className="ml-[26px] py-2">
            <Listbox
              id={`${fieldKey}_limitBillingCountries.countries`}
              name="limitBillingCountries.countries"
              options={countries.map(country => ({
                name: country.name,
                value: country.alpha2,
              }))}
              value={values.limitBillingCountries.countries ?? []}
              onChange={selected => {
                setFieldValue('limitBillingCountries.countries', selected);
              }}
              placeholder="Select billing countries"
              multiple
              selectAll
              selectAllLabel="All countries"
            />
          </div>
        )}

        <FormError name="limitBillingCountries" />

        <div className={clsx(!isDual && 'opacity-50 pointer-events-none')}>
          <Checkbox.Wrapper>
            <Checkbox
              id={`${fieldKey}_limitShippingCountries.enabled`}
              name="limitShippingCountries.enabled"
              disabled={!isDual}
            />
            <Checkbox.Label htmlFor={`${fieldKey}_limitShippingCountries.enabled`}>
              Limit shipping countries
            </Checkbox.Label>
          </Checkbox.Wrapper>

          {isDual && values.limitShippingCountries?.enabled && (
            <div className="ml-[26px] py-2">
              <Listbox
                id={`${fieldKey}_limitShippingCountries.countries`}
                name="limitShippingCountries.countries"
                options={countries.map(country => ({
                  name: country.name,
                  value: country.alpha2,
                }))}
                value={values.limitShippingCountries.countries ?? []}
                onChange={selected => {
                  setFieldValue('limitShippingCountries.countries', selected);
                }}
                placeholder="Select shipping countries"
                multiple
                selectAll
                selectAllLabel="All countries"
              />
            </div>
          )}

          <FormError name="limitShippingCountries" />
        </div>

        <div className={clsx(!isDual && 'opacity-50 pointer-events-none')}>
          <Checkbox.Wrapper>
            <Checkbox
              id={`${fieldKey}_collectShippingPhone`}
              name="collectShippingPhone"
              disabled={!isDual}
            />
            <Checkbox.Label htmlFor={`${fieldKey}_collectShippingPhone`}>
              Collect shipping phone number
            </Checkbox.Label>
          </Checkbox.Wrapper>
        </div>
      </div>
    </div>
  );
}
